import { useCallback } from 'react';

import { useTrackCanvasCreated, useTrackCanvasDraftCreated } from '@air/analytics';
import { useChatSidebarStore } from '@air/feature-ag-ui-chat';
import {
  DEFAULT_CANVAS_TITLE,
  type PendingCreateNode,
  type ResolveCanvasIdResult,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';

import { useOnboardingPreferencesStore } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { type DraftCanvasState } from '~/components/v2/store/shared/createDraftCanvasSlice';
import { useDiscardDraftCanvas } from '~/hooks/canvases/useDiscardDraftCanvas';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getCanvasDetailKey } from '~/swr-hooks/canvases/useCanvas';
import { useCreateCanvas } from '~/swr-hooks/canvases/useCreateCanvas';
import { queryClient } from '~/swr-hooks/queryClient';

// Module-level promise for commit deduplication. Multiple components call useDraftCanvas(),
// each getting their own hook instance. Without a shared variable, concurrent commits from
// different components (e.g. sync queue flush + thread creation) would each create a canvas.
let commitPromise: Promise<ResolveCanvasIdResult> | null = null;

/**
 * Hook for draft canvas mode — defers backend canvas creation until the user
 * takes a meaningful action (adds a node, starts a chat thread).
 *
 * `openDraftCanvas` opens the right panel in draft mode without creating a backend record.
 * `commitDraftCanvas` creates the backend canvas and fires deferred analytics.
 * `discardDraft` cleans up draft state when the panel is closed without committing.
 */
export const useDraftCanvas = () => {
  const draftCanvas = useWorkspaceRightPanelStore((s) => s.draftCanvas);
  const setDraftCanvas = useWorkspaceRightPanelStore((s) => s.setDraftCanvas);
  const setOpen = useWorkspaceRightPanelStore((s) => s.setOpen);
  const setFullscreen = useWorkspaceRightPanelStore((s) => s.setFullscreen);
  const { mutateAsync: createCanvas } = useCreateCanvas();
  const { trackCanvasCreated } = useTrackCanvasCreated();
  const { trackCanvasDraftCreated } = useTrackCanvasDraftCreated();
  const discardDraftCanvas = useDiscardDraftCanvas();

  const { currentWorkspace } = useCurrentWorkspace();

  const openDraftCanvas = useCallback(
    (params: DraftCanvasState) => {
      // Fire abandonment analytics if replacing an existing draft
      discardDraftCanvas();
      // Clear any existing canvas selection so the panel switches to draft mode
      useWorkspaceRightPanelStore.getState().setSelectedCanvasId(null);
      setDraftCanvas(params);
      setOpen(true);
      setFullscreen(false);
      useChatSidebarStore.getState().setOpen(false);
      trackCanvasDraftCreated(params.analyticsContext);

      const workspaceId = currentWorkspace?.id;

      if (workspaceId) {
        useOnboardingPreferencesStore.getState().dismissCanvasPopover(workspaceId);
      }
    },
    [discardDraftCanvas, setDraftCanvas, setOpen, setFullscreen, trackCanvasDraftCreated, currentWorkspace?.id],
  );

  const commitDraftCanvas = useCallback(
    async (pendingCreates: PendingCreateNode[] = [], title = DEFAULT_CANVAS_TITLE): Promise<ResolveCanvasIdResult> => {
      const draft = useWorkspaceRightPanelStore.getState().draftCanvas;

      if (!draft) {
        throw new Error('No draft canvas to commit');
      }

      // Deduplicate: if a commit is already in progress, return the same promise
      if (commitPromise) {
        return commitPromise;
      }

      commitPromise = (async () => {
        try {
          // Signal that we're transitioning — prevents store clearing in useCanvasForRightPanel
          useWorkspaceRightPanelStore.getState().setCommittingDraft(true);

          // Bundle pending node creates with canvas creation
          const nodes =
            pendingCreates.length > 0
              ? pendingCreates.map(({ params }) => ({
                  name: params.name,
                  positionX: params.position.x,
                  positionY: params.position.y,
                  width: params.width,
                  height: params.height,
                  zIndex: params.zIndex,
                  data: params.data,
                }))
              : undefined;

          const canvas = await createCanvas({
            title,
            boardId: draft.boardId,
            nodes,
          });

          // Pre-populate React Query cache to avoid refetch jank
          const workspaceId = currentWorkspace?.id;

          if (workspaceId) {
            queryClient.setQueryData(getCanvasDetailKey({ canvasId: canvas.id, workspaceId }), canvas);
          }

          // Transition from draft to real canvas.
          // Keep committingDraft=true through the re-render so useCanvasForRightPanel
          // skips store clearing. React 18 schedules passive effects as macrotasks via
          // MessageChannel, so requestAnimationFrame (which fires during the browser's
          // rendering phase, after macrotasks) ensures the flag is still true when effects read it.
          useWorkspaceRightPanelStore.getState().setDraftCanvas(null);
          useWorkspaceRightPanelStore.getState().setSelectedCanvasId(canvas.id);
          requestAnimationFrame(() => {
            useWorkspaceRightPanelStore.getState().setCommittingDraft(false);
          });

          // Fire deferred analytics with original attribution
          trackCanvasCreated({
            canvas_id: canvas.id,
            ...draft.analyticsContext,
          });

          // Map response node IDs back to local IDs for remoteId assignment.
          // The API returns nodes in the same order they were sent, so index-based mapping is safe.
          // Only include IDs that actually received remote IDs — if the backend returns fewer
          // nodes than sent, excess creates must remain in the sync queue for retry.
          const count =
            canvas.nodes && pendingCreates.length > 0 ? Math.min(canvas.nodes.length, pendingCreates.length) : 0;
          const createdNodeLocalIds =
            count > 0 ? pendingCreates.slice(0, count).map(({ localId }) => localId) : undefined;

          for (let i = 0; i < count; i++) {
            useEditCanvasStore.getState().updateNodeRemoteId(pendingCreates[i].localId, canvas.nodes[i].id);
          }

          return { canvasId: canvas.id, createdNodeLocalIds };
        } catch (error) {
          useWorkspaceRightPanelStore.getState().setCommittingDraft(false);
          throw error;
        } finally {
          commitPromise = null;
        }
      })();

      return commitPromise;
    },
    [createCanvas, currentWorkspace?.id, trackCanvasCreated],
  );

  const discardDraft = discardDraftCanvas;

  return {
    draftCanvas,
    isDraft: !!draftCanvas,
    openDraftCanvas,
    commitDraftCanvas,
    discardDraft,
  };
};
